import { useActiveCart } from "@faire/deprecated-retailer-visitor-shared/features/Cart/useCart";
import {
  getInitialCheckoutCartSummaryRequest,
  getFirstShippingEstimatePolicy,
  getSecondShippingEstimatePolicy,
  shouldSendSecondCheckoutSummaryRequest,
} from "@faire/deprecated-retailer-visitor-shared/hooks/checkout/checkoutCartSummaryUtils";
import {
  getCheckoutCartRequest,
  getImprovedCartBrandsReadyForCheckout,
  INITIAL_SHIPPING_METHOD_TYPE,
} from "@faire/deprecated-retailer-visitor-shared/hooks/checkout/checkoutCartUtils";
import { getPrefetchedListRetailerInstrumentsRequest } from "@faire/deprecated-retailer-visitor-shared/hooks/checkout/listRetailerInstrumentsUtils";
import { getCartToken } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getCartToken";
import { useBrandCart } from "@faire/deprecated-retailer-visitor-shared/views/brand/BrandCart/hooks/useBrandCart";
import { usePrefetchQuery_escape_hatch as usePrefetchInstruments } from "@faire/web-api--source/endpoints/www/api/instrument/list-retailer-instruments/post-hooks";
import { QueryParameters as CheckoutSummaryParameters } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/checkout-summary/get";
import {
  getQueryKey as getCheckoutSummaryQueryKey,
  usePrefetchQuery as usePrefetchCheckoutSummary,
} from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/checkout-summary/get-hooks";
import { QueryParameters as CartParameters } from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get";
import {
  getQueryKey as getCartQueryKey,
  usePrefetchQuery as usePrefetchCheckoutCart,
} from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import { IBrandCartV2 } from "@faire/web-api--source/faire/carts/IBrandCartV2";
import { ICartV2 } from "@faire/web-api--source/faire/carts/ICartV2";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

const CHECKOUT_CART_QUERY_KEY_SUFFIX = "CHECKOUT_CART_QUERY_KEY";
const CHECKOUT_SUMMARY_QUERY_KEY_SUFFIX = "CHECKOUT_SUMMARY_QUERY_KEY";

/**
 * Creates a prefetched checkout cart query key with the suffix for consistent query management
 */
export const getCartQueryKeyForCheckout = (
  cartToken: string,
  request: CartParameters
) => {
  return [
    ...getCartQueryKey(cartToken, request),
    CHECKOUT_CART_QUERY_KEY_SUFFIX,
  ];
};

/**
 * Creates a prefetched checkout summary query key with the suffix for consistent query management
 */
export const getCheckoutSummaryQueryKeyForCheckout = (
  cartToken: string,
  request: CheckoutSummaryParameters
) => {
  return [
    ...getCheckoutSummaryQueryKey(cartToken, request),
    CHECKOUT_SUMMARY_QUERY_KEY_SUFFIX,
  ];
};

/**
 * This hook prefetches the cart and the checkout summary for the given brand tokens (as long as they are in the active cart).
 * @param cart - The cart to prefetch the checkout for.
 * @returns A function that prefetches the checkout for the given cart and brand tokens.
 */
const usePrefetchCheckout = (cart: ICartV2) => {
  const prefetchCart = usePrefetchCheckoutCart();
  const prefetchCheckoutSummary = usePrefetchCheckoutSummary();
  const prefetchInstruments = usePrefetchInstruments();

  return useCallback(
    async (brandTokens: string[]) => {
      const cartToken = getCartToken();
      // prefetch the checkout cart
      const cartRequest = CartParameters.build(
        getCheckoutCartRequest(brandTokens, INITIAL_SHIPPING_METHOD_TYPE)
      );
      const cartQueryKey = getCartQueryKeyForCheckout(cartToken, cartRequest);
      prefetchCart(cartToken, cartRequest, {
        queryKey: cartQueryKey,
      });

      const activeBrandCarts = cart?.brand_carts.filter(
        (brand) => brand.state === IBrandCartV2.State.READY_FOR_CHECKOUT
      );
      // Instead of waiting for the cart to be prefetched, we can just use the data from the active cart.
      // This will unblock the checkout summary prefetch.
      // If there are no brand tokens, user is checking out the whole cart.
      const brandCarts =
        brandTokens.length > 0
          ? activeBrandCarts?.filter((brand) =>
              brandTokens.includes(brand.brand_token ?? "")
            )
          : activeBrandCarts;

      // compute data for the checkout summary request
      const improvedCartBrandsReadyForCheckout =
        getImprovedCartBrandsReadyForCheckout(brandCarts);
      const firstShippingEstimatePolicy = getFirstShippingEstimatePolicy(
        improvedCartBrandsReadyForCheckout
      );

      // prefetch the first checkout summary request
      const firstCheckoutSummaryRequest = getInitialCheckoutCartSummaryRequest(
        brandTokens,
        firstShippingEstimatePolicy
      );
      const firstCheckoutSummaryQueryKey =
        getCheckoutSummaryQueryKeyForCheckout(
          cartToken,
          firstCheckoutSummaryRequest
        );
      prefetchCheckoutSummary(cartToken, firstCheckoutSummaryRequest, {
        queryKey: firstCheckoutSummaryQueryKey,
      });

      const requiresSecondCheckoutRequest =
        shouldSendSecondCheckoutSummaryRequest(
          improvedCartBrandsReadyForCheckout
        );
      /**
       * If we are sending the second request to get the more precise and longer to compute shipping estimate,
       * we prefetch the second checkout summary request.
       */
      if (requiresSecondCheckoutRequest) {
        const secondShippingEstimatePolicy = getSecondShippingEstimatePolicy(
          improvedCartBrandsReadyForCheckout
        );
        const secondCheckoutSummaryRequest =
          getInitialCheckoutCartSummaryRequest(
            brandTokens,
            secondShippingEstimatePolicy
          );
        const secondCheckoutSummaryQueryKey =
          getCheckoutSummaryQueryKeyForCheckout(
            cartToken,
            secondCheckoutSummaryRequest
          );
        prefetchCheckoutSummary(cartToken, secondCheckoutSummaryRequest, {
          queryKey: secondCheckoutSummaryQueryKey,
        });
      }

      // prefetch the payment instruments
      // Note: we still have to refetch the instruments on Checkout page with proper chargeable total amount.
      // This first fetch is to unblock page load and reduce UPLT
      prefetchInstruments(getPrefetchedListRetailerInstrumentsRequest());
    },
    [prefetchCart, prefetchCheckoutSummary, cart, prefetchInstruments]
  );
};

/**
 * This hook prefetches the cart and the checkout summary based on the active cart.
 * @returns A function that prefetches the checkout summary
 */
export const usePrefetchCheckoutFromCart = () => {
  const { data: activeCart } = useActiveCart();
  return usePrefetchCheckout(activeCart);
};

/**
 * This hook prefetches the cart and the checkout summary based on the given brand cart.
 * @param brandToken - The brand token to prefetch the checkout for.
 * @returns A function that prefetches the checkout for the given brand.
 */
export const usePrefetchCheckoutFromBrand = (brandToken: string) => {
  const { data: cart } = useBrandCart({ brandToken });
  return usePrefetchCheckout(cart);
};

/**
 * This hook provides a function to clear  checkout data from the cache.
 * It clears both the cart and checkout summary queries. Merely invalidating the queries is not enough
 * since it will remain in the cache as stale data, meaning the Checkout page won't refetch it on mount.
 */
export const useInvalidateCheckoutData = () => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    // Remove all cart queries with the suffix from the cache
    queryClient.removeQueries({
      predicate: (query) => {
        return query.queryKey.includes(CHECKOUT_CART_QUERY_KEY_SUFFIX);
      },
    });

    // Remove all checkout summary queries with the suffix
    queryClient.removeQueries({
      predicate: (query) => {
        return query.queryKey.includes(CHECKOUT_SUMMARY_QUERY_KEY_SUFFIX);
      },
    });
  }, [queryClient]);
};
